import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import set from 'lodash/set';
import uniq from 'lodash/uniq';

import { AnyAction } from '@lumapps/redux';
import createSlice, { PayloadAction } from '@lumapps/redux/createSlice';
import { mergeObjectOnly } from '@lumapps/utils/object/mergeObjectOnly';

import { UserListSuccessPayload, UserSlice } from '../types';

const initialState: UserSlice = {
    connectedUser: undefined,
    /**
     * `initialized` is here to avoid mapReduxToAngular to be triggered when another action is
     * dispatch while the user has not been set yet by the angular state,
     * the first __angular__/user/update will set this to `true`.
     */
    initialized: false,
    entities: {},
    userList: {
        ids: [],
        hasMore: true,
        isLoading: false,
        cursor: undefined,
    },
    blobAvatars: {},
};

type ErrorMessage = string;

const domain = 'user';

const { actions, reducer } = createSlice({
    domain,
    initialState,
    reducers: {
        '__angular__/user/update': (state: any, action: AnyAction) => {
            mergeObjectOnly(state, action.newState);
        },
        setConnectedUser(state: any, action: PayloadAction) {
            // eslint-disable-next-line no-param-reassign
            state.connectedUser = Object.assign(state.connectedUser || {}, action.payload);
        },
        /**
         * Mirrors the user JWT into the store. Dispatched by `syncConnectedUserToken`, which is the
         * only sanctioned caller: the token itself lives in the `@lumapps/base-api` user token store.
         */
        setConnectedUserToken: (state: UserSlice | null, action: PayloadAction<string | undefined>) => {
            /**
             * The whole slice is `null` for an anonymous visitor: the server renders no user and
             * nothing has replaced it yet when `syncConnectedUserToken` seeds the mirror.
             *
             * Beyond that, never create a `connectedUser` just to hold a token: an object with only
             * a `token` key would read as "someone is connected" for every selector built on it.
             * Stores where the user lands later (legacy angular) get the token from their own write
             * path instead.
             */
            if (!state?.connectedUser) {
                return;
            }

            set(state, 'connectedUser.token', action.payload);
        },
        acceptCookies: (state: UserSlice, action: PayloadAction<any>) => {
            const { hasAccepted = false } = action.payload;

            set(state, 'connectedUser.settings.hasAcceptedCookies', hasAccepted);
        },
        acceptTermsAndConditions: (state: UserSlice, action: PayloadAction<any>) => {
            set(state, 'connectedUser.settings.hasAcceptedTerms', action.payload.hasAccepted);
        },
        setUserListStatus: (state: UserSlice, action: PayloadAction<boolean>) => {
            set(state, ['userList', 'isLoading'], action.payload);
        },
        fetchUserListSuccess: (state: UserSlice, action: PayloadAction<UserListSuccessPayload>) => {
            const items = action.payload.items || [];
            const listByIds = keyBy(items, 'id');
            set(state, 'entities', { ...state.entities, ...listByIds });

            set(
                state,
                ['userList', 'ids'],
                action.payload.add
                    ? uniq([
                          ...(get(state, ['userList', 'ids'], {}) as UserSlice['userList']['ids']),
                          ...Object.keys(listByIds),
                      ])
                    : Object.keys(listByIds),
            );

            set(state, ['userList', 'isLoading'], false);
            set(state, ['userList', 'cursor'], action.payload.cursor);
            set(state, ['userList', 'hasMore'], action.payload.more);
        },
        fetchUserListError: (state: UserSlice, action: PayloadAction<ErrorMessage>) => {
            set(state, ['userList', 'ids'], initialState.userList.ids);
            set(state, ['userList', 'isLoading'], false);
            set(state, ['userList', 'errorMessage'], action.payload);
        },
        addBlobAvatar: (state: UserSlice, action: PayloadAction<UserSlice['blobAvatars']>) => {
            set(state, ['blobAvatars'], { ...get(state, ['blobAvatars']), ...action.payload });
        },
    },
});

export { actions, reducer, initialState, domain };
